// /sources/archive — read-only list of archived sources with restore
// affordance. Split out of /sources so the everyday view stays clean.
// Filtered server-side by listSources(includeArchived=true) + workspace
// scoping (same rules as /sources).

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Source } from '@datahub/shared';
import { listSources, patchSource } from '../api';
import ThemeToggle from '../components/ThemeToggle';
import WorkspaceSwitcher from '../components/WorkspaceSwitcher';
import { useToast } from '../components/Toast';
import { Table } from '../components/Table';
import TopHeader from '../components/TopHeader';

function RelTime({ iso }: { iso: string }): React.ReactElement {
  const ms = Date.now() - Date.parse(iso);
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.round(s / 60);
  const h = Math.round(m / 60);
  const d = Math.round(h / 24);
  let label: string;
  if (s < 60) label = `${s}s ago`;
  else if (m < 60) label = `${m}m ago`;
  else if (h < 24) label = `${h}h ago`;
  else label = `${d}d ago`;
  return <span title={new Date(iso).toLocaleString()}>{label}</span>;
}

export default function SourcesArchivePage(): React.ReactElement {
  const [sources, setSources] = useState<Source[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  function reload(): void {
    listSources(500, true).then(rows => setSources(rows.filter(s => s.status === 'archived'))).catch(e => setError(String(e)));
  }
  useEffect(() => { reload(); }, []);

  async function restore(sourceId: string): Promise<void> {
    try {
      await patchSource(sourceId, { status: 'active' });
      toast.show('Restored to active');
      reload();
    } catch (e) { toast.show(`Restore failed: ${String(e)}`, { kind: 'error' }); }
  }

  const filtered = useMemo(() => {
    if (!sources) return null;
    const q = query.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter(s => {
      const hay = [s.name, s.source_id, s.slug_alias ?? '', ...Object.entries(s.tags ?? {}).map(([k, v]) => `${k}=${v}`)].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [sources, query]);

  return (
    <div>
      <TopHeader breadcrumb={[{ label: 'Sources', to: '/sources' }]} env="archive" hideLive />

      <div className="shell">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Archive</h1>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-3)' }}>
            {filtered ? `${filtered.length} archived` : 'loading…'}
          </span>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search archive…"
            style={{
              padding: '7px 12px', fontSize: 13, fontFamily: 'var(--ui)',
              background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6,
              outline: 'none', color: 'var(--ink)', minWidth: 280,
            }}
          />
          <Link to="/sources" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)', textDecoration: 'none' }}>← back to active sources</Link>
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
        {!sources && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>}

        {sources && (
          <Table<Source>
            rows={filtered ?? []}
            getRowKey={s => s.source_id}
            onRowClick={s => navigate(`/sources/${s.source_id}`)}
            columns={[
              {
                id: 'name', label: 'name',
                render: s => (
                  <>
                    <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{s.name}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>{s.source_id}</div>
                  </>
                ),
              },
              {
                id: 'archived', label: 'archived', mono: true, width: 140,
                render: s => <span style={{ color: 'var(--ink-2)' }}><RelTime iso={s.updated_at} /></span>,
              },
              {
                id: 'last_event', label: 'last event', mono: true, width: 140,
                render: s => <span style={{ color: s.last_event_at ? 'var(--ink-2)' : 'var(--ink-3)' }}>{s.last_event_at ? <RelTime iso={s.last_event_at} /> : 'never'}</span>,
              },
              {
                id: 'actions', label: '', align: 'right', width: 100, stopPropagation: true,
                render: s => (
                  <button
                    onClick={() => { void restore(s.source_id); }}
                    style={{ padding: '4px 10px', fontSize: 11, fontFamily: 'var(--mono)', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}
                    title="Restore — sets status back to active"
                  >restore</button>
                ),
              },
            ]}
            emptyState={
              <div style={{ padding: 60, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10, color: 'var(--ink-3)', fontSize: 13 }}>
                No archived sources.
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}
